import React, { useState, useEffect } from "react";
import axios from "../utils/axiosInstance";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "../layouts/AuthLayout";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLiteFlow = searchParams.get("source") === "lite";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* --------------------------------------------------
     🔒 HARD RESET ON MOUNT (SECURITY)
  -------------------------------------------------- */
  useEffect(() => {
    setEmail("");
    setPassword("");
    setError("");
    setLoading(false);
  }, []);

  /* --------------------------------------------------
     🔐 Email / Password Login (CORE LOGIC)
  -------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(
        `/api/auth/login`,
        { email, password },
        { headers: { "Content-Type": "application/json" } }
      );

      const { token, user } = res.data;
      if (!token || !user) throw new Error("Invalid login response");

      // 🔑 AuthContext login
      await login(token, user);

      const liteResumeStep = localStorage.getItem("lite_resume_step");

      /* ==================================================
         🔐 ADMIN OVERRIDE
      ================================================== */
      if (user.role === "admin") {
        return navigate("/admin-dashboard");
      }

      /* ==================================================
         🟢 PRIORITY 1: EXPLICIT RESUME STEP (REGISTER FLOW)
      ================================================== */
      if (liteResumeStep === "market-potential") {
        localStorage.setItem("lite_entry_decided", "true");
        localStorage.removeItem("lite_resume_step"); // ✅ FIX
        return navigate("/lite/market-potential");
      }

      if (liteResumeStep === "direction-confirm") {
        localStorage.setItem("lite_entry_decided", "true");
        localStorage.removeItem("lite_resume_step"); // ✅ FIX
        return navigate("/lite/direction-confirm");
      }

      /* ==================================================
   🟢 PRIORITY 2: LITE JOURNEY RESUME (CRITICAL)
   ✅ ONLY run if coming from Lite flow
================================================== */
if (isLiteFlow) {
  const liteData =
  localStorage.getItem(`lite_journey_${user._id}`) ||
  localStorage.getItem("lite_journey_guest");

  if (liteData) {
    try {
      const parsed = JSON.parse(liteData);

      const completedDays = Array.isArray(parsed.completedDays)
        ? parsed.completedDays
        : [];

      const lastCompleted = completedDays.length
        ? Math.max(...completedDays)
        : 0;

      const currentDay = Math.min(lastCompleted + 1, 7);

      // ✅ CRITICAL FIX:
      // remove stale upgrade redirect intent
      localStorage.removeItem("post_auth_intent");
      sessionStorage.removeItem("upgrade_intent_consumed");

      // ✅ required by Lite guards
      localStorage.setItem("lite_entry_decided", "true");

      if (currentDay === 1) {
        return navigate("/lite/mentor");
      }

      if (currentDay === 2) {
        return navigate("/lite/purpose");
      }

      if (currentDay === 3) {
        return navigate("/lite/idea");
      }

      if (currentDay === 4) {
        return navigate("/lite/direction-confirm");
      }

      if (currentDay === 5) {
        return navigate("/lite/market-potential");
      }

      if (currentDay === 6) {
        return navigate("/lite/learning");
      }

      if (currentDay >= 7) {
        return navigate("/lite/day-7");
      }

    } catch (e) {
      console.warn("Lite journey parse failed");
    }
  }
}

      /* ==================================================
         🔵 NORMAL ROLE ROUTING (PRO SAFE)
      ================================================== */
      switch (user.role) {
        case "investor":
          return navigate("/investor-dashboard");

        case "mentor":
          return navigate("/mentor-dashboard");

        case "founder":
        case "user":
        default:
          return navigate("/idea-generation"); // ✅ unchanged
      }

    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Invalid email or password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------
     🔵 Google Login (UNCHANGED)
  -------------------------------------------------- */
  const handleGoogleLogin = () => {
    alert(
      "Google sign-in coming soon.\n\nAfter sign-in, you’ll choose your role."
    );
  };

  return (
    <AuthLayout
      key="login"
      title="Sign in to MDIH"
      subtitle="Continue your innovation journey"
    >
      {/* GOOGLE SIGN-IN */}
      <button
        onClick={handleGoogleLogin}
        className="w-full flex items-center justify-center gap-2 border rounded-md py-3 text-sm font-medium hover:bg-gray-50 transition"
      >
        Continue with Google
      </button>

      {/* DIVIDER */}
      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400">or continue with email</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* ERROR */}
      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* FORM */}
      <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
        <input
          type="email"
          name="email"
          autoComplete="new-email"
          placeholder="Email address"
          className="w-full border rounded-md px-3 py-2.5 text-sm"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <input
          type="password"
          name="password"
          autoComplete="new-password"
          placeholder="Password"
          className="w-full border rounded-md px-3 py-2.5 text-sm"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2.5 rounded-md text-sm font-medium text-white transition ${
            loading
              ? "bg-blue-400"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>

      {/* REGISTER */}
      <p className="text-sm text-gray-600 text-center mt-5">
        New to MDIH?{" "}
        <button
          onClick={() => navigate("/register")}
          type="button"
          className="text-blue-600 hover:underline font-medium"
        >
          Create an account
        </button>
      </p>
    </AuthLayout>
  );
};

export default Login;